import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bookmark, Trash2, ShoppingCart, Calculator, Sparkles, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useStore } from "@/lib/store";
import { inr, fmtDate } from "@/components/badges";

const ICONS = { calculator: Calculator, ai: Sparkles, bom: Upload, cart: ShoppingCart };

export default function SavedListsPage() {
  const [lists, setLists] = useState(null);
  const { addToCart } = useStore();

  const load = () => api.get("/lists").then((r) => setLists(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try { await api.delete(`/lists/${id}`); toast.success("List deleted"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const reorder = async (id) => {
    try {
      const { data } = await api.get(`/lists/${id}`);
      const valid = data.items.filter((i) => i.product);
      if (!valid.length) { toast.error("None of these items are available right now"); return; }
      const vendor = valid[0].product.vendor_id;
      const same = valid.filter((i) => i.product.vendor_id === vendor);
      same.forEach((i) => addToCart(i.product, i.qty));
      if (same.length < valid.length) toast.info(`${valid.length - same.length} item(s) from other vendors skipped — single-vendor cart.`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="saved-lists-page">
      <p className="overline-label">Saved material lists</p>
      <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 mt-1">Project lists</h1>
      <p className="text-sm text-slate-500 mt-1">Save calculator, AI or BOM results per project and reorder in one tap.</p>
      {lists === null ? <p className="py-12 text-center text-sm text-slate-500">Loading…</p> : lists.length === 0 ? (
        <div className="card-surface border-dashed p-12 text-center mt-8" data-testid="lists-empty">
          <Bookmark className="h-8 w-8 text-slate-300 mx-auto" />
          <p className="mt-3 text-sm text-slate-500">No lists yet. Run the <Link to="/tools" className="text-orange-700 font-semibold">calculator or AI recommender</Link> and hit "Save as list".</p>
        </div>
      ) : (
        <div className="mt-8 grid md:grid-cols-2 gap-4">
          {lists.map((l) => {
            const Icon = ICONS[l.source] || Bookmark;
            return (
              <div key={l.id} className="card-surface p-5 flex flex-col" data-testid={`list-card-${l.id}`}>
                <div className="flex items-start gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-900 shrink-0"><Icon className="h-4 w-4 text-amber-400" /></span>
                  <div className="min-w-0 flex-1">
                    <p className="font-display font-semibold text-slate-900 truncate">{l.name}</p>
                    <p className="text-xs text-slate-500">{l.items.length} items · from {l.source} · {fmtDate(l.created_at)}</p>
                  </div>
                  <button onClick={() => remove(l.id)} data-testid={`list-delete-${l.id}`} className="p-1.5 text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </div>
                <ul className="mt-3 space-y-1 text-xs text-slate-600 flex-1">
                  {l.items.slice(0, 5).map((i) => <li key={i.product_id} className="flex justify-between gap-2"><span className="truncate">{i.name}</span><span className="font-mono2 text-slate-400 shrink-0">× {i.qty}</span></li>)}
                  {l.items.length > 5 && <li className="text-slate-400">+{l.items.length - 5} more</li>}
                </ul>
                <button onClick={() => reorder(l.id)} data-testid={`list-reorder-${l.id}`} className="btn-brand w-full mt-4 !py-2.5 text-xs"><ShoppingCart className="h-3.5 w-3.5" /> Add all to cart</button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
